import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import AdminDashboardModal from "../adminDashboard/adminDashboardModal";
import ReactApexChart from "react-apexcharts";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { fetchTeacherDashboardApi } from "../../../core/data/services/teacherDashboardService";
import {
	setError,
} from "../../../core/data/redux/teacherDashboardSlice";
import GeoLocationModal from "../../../core/common/GeoLocationModal";
import { useGeoLocationModal } from "../../../core/hooks/useGeoLocationModal";
import { getSchoolById } from "../../../core/data/services/schoolService";
import { setSelectedSchool } from "../../../core/data/redux/schoolSlice";
import { RootState } from "../../../core/data/redux/store";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { TeacherAttendance } from "../../../core/data/types/teacherAtteandance";
import {
	fetchTeacherAttendanceStart,
	fetchTeacherAttendanceSuccess,
	fetchTeacherAttendanceError,
} from '../../../core/data/redux/teacherAtteandanceSlice';
import { fetchTeacherAttendanceApi, checkOutTeacherAttnApi, checkInTeacherAttnApi } from '../../../core/data/services/teacherAttendanceService';
import ErrorModal from "../../../core/common/ErrorModal";

const TeacherDashboard = () => {
	const routes = all_routes;
	const dispatch = useDispatch();
	const hasFetched = useRef(false);
	// const { selectedSchool } = useSelector((state: RootState) => state.school)

	// Helper function to convert number to Roman numerals
	const convertToRoman = (num: number): string => {
		if (isNaN(num)) return "";
		const romanNumerals: [string, number][] = [
			["M", 1000],
			["CM", 900],
			["D", 500],
			["CD", 400],
			["C", 100],
			["XC", 90],
			["L", 50],
			["XL", 40],
			["X", 10],
			["IX", 9],
			["V", 5],
			["IV", 4],
			["I", 1],
		];
		let result = "";
		for (const [roman, value] of romanNumerals) {
			while (num >= value) {
				result += roman;
				num -= value;
			}
		}
		return result;
	};

	// Get location verification status from Redux store
	const userLocation = useSelector((state: RootState) => state.userLocation);
	const errorMessage = useSelector((state: RootState) => state.teacherDashboard.error);
	const isLocationVerified = useSelector((state: RootState) => state.userLocation.isLocationVerified);

	// Geolocation modal hook
	const {
		showGeoLocationModal,
		setShowGeoLocationModal,
	} = useGeoLocationModal();

	// Global Filter States
	const [periodType, setPeriodType] = useState<'day' | 'week' | 'month' | 'year' | 'custom'>('day');
	const [globalStartDate, setGlobalStartDate] = useState<Date>(dayjs().startOf("day").toDate());
	const [globalEndDate, setGlobalEndDate] = useState<Date>(dayjs().endOf("day").toDate());
	const [isGlobalFilterActive, setIsGlobalFilterActive] = useState(true);

	// Individual filter states
	const [attendanceDateRange, setAttendanceDateRange] = useState<"today" | "thisWeek" | "lastWeek" | "lastMonth" | "custom">("today");
	const [mealsDateRange, setMealsDateRange] = useState<"today" | "thisWeek" | "lastWeek" | "lastMonth" | "custom">("today");
	const [calendarDate, setCalendarDate] = useState<Nullable<Date>>(null);
	const [customAttendanceStartDate, setCustomAttendanceStartDate] = useState<Nullable<Date>>(null);
	const [customAttendanceEndDate, setCustomAttendanceEndDate] = useState<Nullable<Date>>(null);
	const [customMealsStartDate, setCustomMealsStartDate] = useState<Nullable<Date>>(null);
	const [customMealsEndDate, setCustomMealsEndDate] = useState<Nullable<Date>>(null);

	const [attendanceData, setAttendanceData] = useState({ studentsPresent: 0, studentsAbsent: 0 });
	const [mealsData, setMealsData] = useState({ mealsServedCount: 0, melasNotServedCount: 0 });
	const [classListData, setClassListData] = useState<any[]>([]);
	const [attendanceLoading, setAttendanceLoading] = useState(false);
	const [mealsLoading, setMealsLoading] = useState(false);
	const [classListLoading, setClassListLoading] = useState(false);

	const [attendanceDropdownOpen, setAttendanceDropdownOpen] = useState(false);
	const [mealsDropdownOpen, setMealsDropdownOpen] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
	const [isCheckedIn, setIsCheckedIn] = useState(false);

	const [formData, setFormData] = useState<TeacherAttendance[]>([]);
	const [isProcessing, setIsProcessing] = useState(false);

	// Get user data from Redux
	const user = useSelector(
		(state: RootState) => state.user
	);

	const teacherAttn = useSelector(
		(state: RootState) => state.teacherAttendance.attendance
	);

	const schoolId = user.Users[0].relatedEntity?.schools?.id;
	const teacherId = user.Userdetail?.id;

	// Memoize the teacher's class list to prevent unnecessary re-computation
	const teacherClassList = useMemo(() => {
		const classes = user?.Users[0]?.relatedEntity?.schoolClasses;
		return (Array.isArray(classes) ? classes : []) as any[];
	}, [user?.Users]);

	// Memoize the formatted list to prevent unnecessary re-computation
	const formattedList = useMemo(() => {
		const list: any[] = [];
		if (teacherClassList && teacherClassList.length > 0) {
			(teacherClassList as any[]).forEach((item: any) => {
				if (item.classSection && Array.isArray(item.classSection)) {
					item.classSection.forEach((section: any) => {
						list.push({
							className: item.schoolClass?.name || 'Unknown Class',
							classSectionId: String(section?.id || ''),
							schoolId: String(item.schoolClass?.id || ''),
							sectionName: section?.name || 'Unknown Section'
						});
					});
				}
			});
		}
		return list;
	}, [teacherClassList]);

	// Memoize the transformed data to prevent unnecessary re-computation
	const transformedData = useMemo(() => {
		return classListData?.map((item: any, index: number) => {
			const checkExists = formattedList?.find((x: any) => x?.classSectionId === item?.classSectionId);
			const className = item.className || item.class || item.grade || `Class ${index + 1}`;
			const sectionName = item.sectionName || item.section || item.div || 'A';
			const progress = item.progress || item.attendancePercentage || Math.floor(Math.random() * 100);
			const avatars = item.avatars || item.studentAvatars || [];

			const classNumber = parseInt(className.toString().replace(/\D/g, ''), 10);
			const displayClassName = isNaN(classNumber) ? className : convertToRoman(classNumber);

			// Only return items that exist in formattedList (teacher's assigned classes)
			if (!checkExists) {
				return null;
			}

			return {
				className: displayClassName,
				sectionName: sectionName,
				progress: progress,
				avatars: avatars,
				classSectionId: item.classSectionId
			};
		}).filter(Boolean); // Remove null values
	}, [classListData, formattedList]); // Only re-compute when these dependencies change

	// Load school data when component mounts
	useEffect(() => {
		const loadSchoolData = async () => {
			try {
				const school = await getSchoolById(schoolId);
				if (school) {
					dispatch(setSelectedSchool(school));
				} else {
					console.error('School not found for user:', user.Users[0]?.relatedEntity?.schools?.name);
				}
			} catch (error) {
				console.error('Error loading school data:', error);
			}
		};

		if (user && user.Users.length > 0) {
			loadSchoolData();
		}
	}, [dispatch, user, schoolId]);

	// Handle location verification result
	const handleLocationVerified = (isWithinRadius: boolean, distance: number) => {
		if (isWithinRadius) {
			console.log(`Location verified! Distance: ${distance}m`);
		} else {
			console.log(`Location verification failed! Distance: ${distance}m`);
		}
	};

	const getDatesByRange = (range: string, customStart?: Date | null, customEnd?: Date | null) => {
		const today = dayjs();

		if (range === "custom" && customStart && customEnd) {
			return {
				startDate: dayjs(customStart).format("YYYY-MM-DD"),
				endDate: dayjs(customEnd).format("YYYY-MM-DD")
			};
		}

		switch (range) {
			case "today":
				return { startDate: today.format("YYYY-MM-DD"), endDate: today.format("YYYY-MM-DD") };
			case "thisWeek":
				return {
					startDate: today.startOf("week").format("YYYY-MM-DD"),
					endDate: today.endOf("week").format("YYYY-MM-DD"),
				};
			case "lastWeek":
				return {
					startDate: today.subtract(1, "week").startOf("week").format("YYYY-MM-DD"),
					endDate: today.subtract(1, "week").endOf("week").format("YYYY-MM-DD"),
				};
			case "lastMonth":
				return {
					startDate: today.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
					endDate: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
				};
			default:
				return { startDate: today.format("YYYY-MM-DD"), endDate: today.format("YYYY-MM-DD") };
		}
	};

	const fetchAttendanceData = useCallback(async (startDate: string, endDate: string) => {
		try {
			setAttendanceLoading(true);
			const res = await fetchTeacherDashboardApi({
				startDate,
				endDate,
				schoolIds: schoolId,
				isCumulative: true
			});

			if (res && res.cumulative) {
				setAttendanceData({
					studentsPresent: res.cumulative.studentsPresent || 0,
					studentsAbsent: res.cumulative.studentsAbsent || 0
				});
			} else {
				console.warn("Unexpected attendance data structure:", res);
				setAttendanceData({ studentsPresent: 0, studentsAbsent: 0 });
			}
		} catch (error: any) {
			dispatch(setError(error.message));
			console.error("Error fetching attendance data:", error);
			setAttendanceData({ studentsPresent: 0, studentsAbsent: 0 });
		} finally {
			setAttendanceLoading(false);
		}
	}, [dispatch, schoolId]);

	const fetchMealsData = useCallback(async (startDate: string, endDate: string) => {
		try {
			setMealsLoading(true);
			const res = await fetchTeacherDashboardApi({
				startDate,
				endDate,
				schoolIds: schoolId,
				isCumulative: true
			});

			if (res && res.cumulative) {
				setMealsData({
					mealsServedCount: res.cumulative.mealsServedCount || 0,
					melasNotServedCount: res.cumulative.melasNotServedCount || 0
				});
			} else {
				console.warn("Unexpected meals data structure:", res);
				setMealsData({ mealsServedCount: 0, melasNotServedCount: 0 });
			}
		} catch (error: any) {
			dispatch(setError(error.message));
			console.error("Error fetching meals data:", error);
			setMealsData({ mealsServedCount: 0, melasNotServedCount: 0 });
		} finally {
			setMealsLoading(false);
		}
	}, [dispatch, schoolId]);

	const fetchClassListData = useCallback(async (startDate: string, endDate: string) => {
		try {
			setClassListLoading(true);
			const res = await fetchTeacherDashboardApi({
				startDate,
				endDate,
				schoolIds: schoolId,
				isCumulative: false
			});

			let classData = [];

			if (res) {
				if (Array.isArray(res)) {
					classData = res;
				}
				else {
					const possibleArrays = Object.values(res).filter(val => Array.isArray(val));
					if (possibleArrays.length > 0) {
						classData = possibleArrays[0];
					}
				}
			}

			setClassListData(Array.isArray(classData) ? classData : []);
		} catch (error: any) {
			dispatch(setError(error.message));
			console.error("Error fetching class list data:", error);
			setClassListData([]);
		} finally {
			setClassListLoading(false);
		}
	}, [dispatch, schoolId]);

	// Function to fetch all data
	const fetchAllData = useCallback((startDate: string, endDate: string) => {
		fetchAttendanceData(startDate, endDate);
		fetchMealsData(startDate, endDate);
		fetchClassListData(startDate, endDate);
	}, [fetchAttendanceData, fetchMealsData, fetchClassListData]);

	// Global Filter Handlers
	useEffect(() => {
		if (periodType === 'custom') return;

		let newStart: any, newEnd: any;
		if (periodType === 'day') {
			newStart = dayjs().toDate();
			newEnd = dayjs().toDate();
		} else if (periodType === 'week') {
			newStart = dayjs().startOf('week').toDate();
			newEnd = dayjs().endOf('week').toDate();
		} else if (periodType === 'month') {
			newStart = dayjs().startOf('month').toDate();
			newEnd = dayjs().endOf('month').toDate();
		} else if (periodType === 'year') {
			newStart = dayjs().startOf('year').toDate();
			newEnd = dayjs().endOf('year').toDate();
		}

		setGlobalStartDate(newStart);
		setGlobalEndDate(newEnd);
		setIsGlobalFilterActive(true);

		const startStr = dayjs(newStart).format('YYYY-MM-DD');
		const endStr = dayjs(newEnd).format('YYYY-MM-DD');
		fetchAllData(startStr, endStr);
	}, [periodType, fetchAllData]);

	const handlePrev = () => {
		let newStart: Date, newEnd: Date;
		if (periodType === 'day') {
			newStart = dayjs(globalStartDate).subtract(1, 'day').toDate();
			newEnd = dayjs(globalEndDate).subtract(1, 'day').toDate();
		} else if (periodType === 'week') {
			newStart = dayjs(globalStartDate).subtract(1, 'week').toDate();
			newEnd = dayjs(globalEndDate).subtract(1, 'week').toDate();
		} else if (periodType === 'month') {
			newStart = dayjs(globalStartDate).subtract(1, 'month').toDate();
			newEnd = dayjs(globalEndDate).subtract(1, 'month').toDate();
		} else if (periodType === 'year') {
			newStart = dayjs(globalStartDate).subtract(1, 'year').toDate();
			newEnd = dayjs(globalEndDate).subtract(1, 'year').toDate();
		} else {
			const diff = dayjs(globalEndDate).diff(dayjs(globalStartDate), 'days');
			newStart = dayjs(globalStartDate).subtract(diff + 1, 'days').toDate();
			newEnd = dayjs(globalEndDate).subtract(diff + 1, 'days').toDate();
		}

		setGlobalStartDate(newStart);
		setGlobalEndDate(newEnd);

		const startStr = dayjs(newStart).format('YYYY-MM-DD');
		const endStr = dayjs(newEnd).format('YYYY-MM-DD');
		fetchAllData(startStr, endStr);
	};

	const handleNext = () => {
		let newStart: Date, newEnd: Date;
		if (periodType === 'day') {
			newStart = dayjs(globalStartDate).add(1, 'day').toDate();
			newEnd = dayjs(globalEndDate).add(1, 'day').toDate();
		} else if (periodType === 'week') {
			newStart = dayjs(globalStartDate).add(1, 'week').toDate();
			newEnd = dayjs(globalEndDate).add(1, 'week').toDate();
		} else if (periodType === 'month') {
			newStart = dayjs(globalStartDate).add(1, 'month').toDate();
			newEnd = dayjs(globalEndDate).add(1, 'month').toDate();
		} else if (periodType === 'year') {
			newStart = dayjs(globalStartDate).add(1, 'year').toDate();
			newEnd = dayjs(globalEndDate).add(1, 'year').toDate();
		} else {
			const diff = dayjs(globalEndDate).diff(dayjs(globalStartDate), 'days');
			newStart = dayjs(globalStartDate).add(diff + 1, 'days').toDate();
			newEnd = dayjs(globalEndDate).add(diff + 1, 'days').toDate();
		}

		setGlobalStartDate(newStart);
		setGlobalEndDate(newEnd);

		const startStr = dayjs(newStart).format('YYYY-MM-DD');
		const endStr = dayjs(newEnd).format('YYYY-MM-DD');
		fetchAllData(startStr, endStr);
	};

	const handleGlobalDateChange = (which: 'start' | 'end', value: string) => {
		if (which === 'start') {
			const newDate = value ? new Date(value) : globalStartDate;
			setGlobalStartDate(newDate);
		} else {
			const newDate = value ? new Date(value) : globalEndDate;
			setGlobalEndDate(newDate);
		}
		setPeriodType('custom');
		setIsGlobalFilterActive(true);

		setTimeout(() => {
			const startStr = dayjs(which === 'start' ? (value ? new Date(value) : globalStartDate) : globalStartDate).format('YYYY-MM-DD');
			const endStr = dayjs(which === 'end' ? (value ? new Date(value) : globalEndDate) : globalEndDate).format('YYYY-MM-DD');
			fetchAllData(startStr, endStr);
		}, 100);
	};

	// Effect for individual attendance data
	useEffect(() => {
		if (isGlobalFilterActive || calendarDate) return; // Skip if global filter or calendar is active

		const { startDate, endDate } = getDatesByRange(attendanceDateRange, customAttendanceStartDate, customAttendanceEndDate);
		fetchAttendanceData(startDate, endDate);
	}, [attendanceDateRange, customAttendanceStartDate, customAttendanceEndDate, fetchAttendanceData, isGlobalFilterActive, calendarDate]);

	// Effect for individual meals data
	useEffect(() => {
		if (isGlobalFilterActive || calendarDate) return; // Skip if global filter or calendar is active

		const { startDate, endDate } = getDatesByRange(mealsDateRange, customMealsStartDate, customMealsEndDate);
		fetchMealsData(startDate, endDate);
	}, [mealsDateRange, customMealsStartDate, customMealsEndDate, fetchMealsData, isGlobalFilterActive, calendarDate]);

	// Effect for calendar selection
	useEffect(() => {
		if (!calendarDate) return;

		const selectedDate = dayjs(calendarDate).format("YYYY-MM-DD");
		setIsGlobalFilterActive(false); // Deactivate global filter when calendar is used
		fetchAllData(selectedDate, selectedDate);
	}, [calendarDate, fetchAllData]);

	// Initial data load
	useEffect(() => {
		const today = dayjs().format("YYYY-MM-DD");
		fetchAllData(today, today);
	}, [fetchAllData]);

	const fetchTeacherAttendanceData = useCallback(async () => {
  try {
    dispatch(fetchTeacherAttendanceStart(true));
    const payload = {
      schoolId,
      teacherId,
      attnDate: new Date().toISOString().split('T')[0], // Use today's date
    };
    const teacherAttendance = await fetchTeacherAttendanceApi(payload);
    dispatch(fetchTeacherAttendanceSuccess({ data: teacherAttendance }));
    setFormData(Array.isArray(teacherAttendance) ? teacherAttendance : [teacherAttendance]);

    if (
      teacherAttendance.length > 0 &&
      teacherAttendance[0].clockInTime &&
      teacherAttendance[0].clockInTime !== "" &&
      (!teacherAttendance[0].clockOutTime || teacherAttendance[0].clockOutTime === null)
    ) {
      setIsCheckedIn(true);
    } else {
      setIsCheckedIn(false);
    }

    return teacherAttendance;
  } catch (error) {
    const msg = (error && typeof error === 'object' && 'message' in error) ? (error as any).message : 'Failed to fetch attendance data.';
    dispatch(fetchTeacherAttendanceError(msg));
    setError(msg);
    setShowErrorModal(true);
    console.error('Error fetching data:', msg);
    throw error;
  }
}, [dispatch, schoolId, teacherId, setFormData, setIsCheckedIn]);

useEffect(() => {
  if (hasFetched.current || !schoolId) return;
  hasFetched.current = true;
  fetchTeacherAttendanceData();
}, [dispatch, schoolId, teacherId, fetchTeacherAttendanceData]);

	useEffect(() => {
		if (teacherAttn.length > 0 && teacherAttn[0].clockOutTime === null) {
			setIsCheckedIn(true);  // Already checked in
		} else {
			setIsCheckedIn(false); // Not checked in or already checked out
		}
	}, [teacherAttn]);

	const teacherAttnCheckIn = async () => {
		if (isProcessing) return; // Prevent multiple clicks

		try {
			setIsProcessing(true);
			const todayDateTime = new Date().toISOString();
			const todayDate = todayDateTime.split("T")[0]; // Get only the date part

			const attendancePayload = {
				id: '',
				teacherId: teacherId ?? 0,
				schoolId: schoolId ?? "",
				attnDate: todayDate,
				clockInTime: todayDateTime,
				clockOutTime: null,
				attendance: true,
				attnTypeId: 1,
				latitude: userLocation?.currentUserLocation?.latitude ?? 0,
				longitude: userLocation?.currentUserLocation?.longitude ?? 0,
				createdAt: "",
				updatedAt: "",
				createdBy: teacherId ?? "",
				updatedBy: teacherId ?? "",
				firstName: "",
				lastName: "",
				designation: "",
				email: "",
			};

			// Call check-in API
			await checkInTeacherAttnApi(attendancePayload);

			// Refetch the updated data from server
			// const today = dayjs().format("YYYY-MM-DD");
			await fetchTeacherAttendanceData();

		} catch (error: any) {
			const msg = error && typeof error === 'object' && 'message' in error ? (error as any).message : 'Failed to fetch attendance data for check-in.';
			dispatch(fetchTeacherAttendanceError(msg));
			dispatch(setError(msg));
			setShowErrorModal(true);
		} finally {
			setIsProcessing(false);
		}
	};

	const teacherAttnCheckOut = async () => {
		if (isProcessing) return; // Prevent multiple clicks

		try {
			setIsProcessing(true);
			const todayDateTime = new Date().toISOString();
			const todayDate = todayDateTime.split("T")[0]; // Get only the date part

			const attendancePayload = {
				id: formData[0].id,
				teacherId: teacherId ?? 0,
				schoolId: schoolId ?? "",
				attnDate: todayDate,
				clockInTime: formData[0].clockInTime,
				clockOutTime: todayDateTime,
				attendance: true,
				attnTypeId: 2,
				latitude: formData[0].latitude,
				longitude: formData[0].longitude,
				createdAt: formData[0].createdAt,
				updatedAt: todayDateTime,
				createdBy: formData[0].createdBy,
				updatedBy: teacherId ?? "",
				firstName: formData[0].firstName,
				lastName: formData[0].lastName,
				designation: formData[0].designation,
				email: formData[0].email,
			};

			// Call check-out API
			await checkOutTeacherAttnApi(attendancePayload);

			// Refetch the updated data from server
			await fetchTeacherAttendanceData();

		} catch (error) {
			const msg = (error && typeof error === 'object' && 'message' in error) ? (error as any).message : 'Failed to check out.';
			console.error('Error checking out:', msg);
			setError(msg);
			setShowErrorModal(true);
		} finally {
			setIsProcessing(false);
		}
	};

	const attendanceChartOpts: any = {
		chart: { height: 290, type: "donut" as const, toolbar: { show: false } },
		dataLabels: { enabled: false },
		series: [attendanceData.studentsPresent, attendanceData.studentsAbsent],
		labels: ["Present", "Absent"],
		colors: ["#1ABE17", "#E82646"],
		legend: { position: "bottom" as const },
		plotOptions: {
			pie: {
				donut: {
					labels: {
						show: true,
						total: {
							show: true,
							label: 'Total',
							formatter: () => {
								return (attendanceData.studentsPresent + attendanceData.studentsAbsent).toString();
							}
						}
					}
				}
			}
		}
	};

	const mealsChartOpts: any = {
		chart: { height: 290, type: "donut" as const, toolbar: { show: false } },
		dataLabels: { enabled: false },
		series: [mealsData.mealsServedCount, mealsData.melasNotServedCount],
		labels: ["Served", "Not Served"],
		colors: ["#1ABE17", "#E82646"],
		legend: { position: "bottom" as const },
		plotOptions: {
			pie: {
				donut: {
					labels: {
						show: true,
						total: {
							show: true,
							label: 'Total',
							formatter: () => {
								return (mealsData.mealsServedCount + mealsData.melasNotServedCount).toString();
							}
						}
					}
				}
			}
		}
	};

	const displayText = {
		today: "Today",
		thisWeek: "This Week",
		lastWeek: "Last Week",
		lastMonth: "Last Month",
		custom: "Custom Range",
	} as const;

	const handleAttendanceRangeChange = (range: string) => {
		setAttendanceDateRange(range as any);
		setCalendarDate(null);
		setIsGlobalFilterActive(false);
		setAttendanceDropdownOpen(false);

		if (range !== "custom") {
			setCustomAttendanceStartDate(null);
			setCustomAttendanceEndDate(null);
		}
	};

	const handleMealsRangeChange = (range: string) => {
		setMealsDateRange(range as any);
		setCalendarDate(null);
		setIsGlobalFilterActive(false);
		setMealsDropdownOpen(false);

		if (range !== "custom") {
			setCustomMealsStartDate(null);
			setCustomMealsEndDate(null);
		}
	};

	const handleCalendarChange = (e: any) => {
		setCalendarDate(e.value);
		setIsGlobalFilterActive(false);
	};

	const getDisplayText = (range: string, customStart?: Date | null, customEnd?: Date | null) => {
		if (isGlobalFilterActive && globalStartDate && globalEndDate) {
			return `Global: ${dayjs(globalStartDate).format("MMM DD")} - ${dayjs(globalEndDate).format("MMM DD, YYYY")}`;
		}
		if (calendarDate) {
			return dayjs(calendarDate).format("MMM DD, YYYY");
		}
		if (range === "custom" && customStart && customEnd) {
			return `${dayjs(customStart).format("MMM DD")} - ${dayjs(customEnd).format("MMM DD, YYYY")}`;
		}
		return displayText[range as keyof typeof displayText];
	};

	const renderClassList = () => {
		if (classListLoading) {
			return (
				<div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
					<div className="spinner-border" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
				</div>
			);
		}

		if (!classListData || classListData.length === 0) {
			return (
				<div className="text-center py-4">
					<p className="text-muted">No class data available for the selected date.</p>
					<small className="text-info">
						API Response Debug: Check console for response structure
					</small>
				</div>
			);
		}

		return (
			<div className="card-body pb-1">
				{transformedData?.map((item: any, idx: number) => (
					<div className="d-sm-flex align-items-center mb-1" key={idx}>
						<div className="w-40 mb-1">
							<h6>Class {item?.className}, {item?.sectionName}</h6>
						</div>
						<div className="class-progress w-100 ms-sm-3 mb-1">
							<div
								className="progress justify-content-between"
								role="progressbar"
								aria-valuenow={item?.progress}
								aria-valuemin={0}
								aria-valuemax={100}
							>
								<div
									className="progress-bar bg-primary"
									style={{ width: `${item?.progress}%` }}
								>
									<div className="avatar-list-stacked avatar-group-xs d-flex">
										{item?.avatars?.map((avatar: string, index: number) => (
											<span key={index} className="avatar avatar-rounded">
												<img
													className="border border-white"
													src={avatar}
													alt="student"
													style={{ width: '24px', height: '24px', objectFit: 'cover' }}
												/>
											</span>
										))}
									</div>
								</div>
								<span className="badge">{item?.progress}%</span>
							</div>
						</div>
					</div>
				))}
			</div>
		);
	};

	return (
		<>
			<div className="page-wrapper">
				<div className="content">
					{/* Header */}
					<div className="d-md-flex align-items-center justify-content-between mb-4">
						<div>
							<h3 className="page-title mb-1">Teacher Dashboard</h3>
							<nav>
								<ol className="breadcrumb mb-0">
									<li className="breadcrumb-item">
										<Link to={routes.teacherDashboard}>Dashboard</Link>
									</li>
									<li className="breadcrumb-item active">Teacher Dashboard</li>
								</ol>
							</nav>
						</div>
					</div>

					{/* Global Date Filter */}
					<div className="card mb-4">
						<div className="card-body py-3">
							<div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
								<div className="d-flex align-items-center gap-3">
									{/* Period Type Tabs */}
									<div className="btn-group" role="group">
										<button
											type="button"
											className={`btn ${periodType === 'day' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
											onClick={() => setPeriodType('day')}
										>
											Day
										</button>
										<button
											type="button"
											className={`btn ${periodType === 'week' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
											onClick={() => setPeriodType('week')}
										>
											Week
										</button>
										<button
											type="button"
											className={`btn ${periodType === 'month' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
											onClick={() => setPeriodType('month')}
										>
											Month
										</button>
										<button
											type="button"
											className={`btn ${periodType === 'year' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
											onClick={() => setPeriodType('year')}
										>
											Year
										</button>
									</div>

									{/* Navigation Arrows */}
									<div className="d-flex align-items-center gap-2">
										<button
											className="btn btn-outline-secondary btn-sm"
											onClick={handlePrev}
											title="Previous"
										>
											<i className="ti ti-chevron-left"></i>
										</button>
										<button
											className="btn btn-outline-secondary btn-sm"
											onClick={handleNext}
											title="Next"
										>
											<i className="ti ti-chevron-right"></i>
										</button>
									</div>
								</div>

								{/* Date Range Inputs */}
								<div className="d-flex align-items-center gap-2">
									<div className="position-relative">
										<input
											type="date"
											className="form-control form-control-sm"
											style={{ width: '150px', paddingRight: '35px' }}
											value={globalStartDate ? dayjs(globalStartDate).format("YYYY-MM-DD") : ""}
											max={globalEndDate ? dayjs(globalEndDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")}
											onChange={(e) => handleGlobalDateChange("start", e.target.value)}
										/>
										<i className="ti ti-calendar position-absolute" style={{
											right: '10px',
											top: '50%',
											transform: 'translateY(-50%)',
											pointerEvents: 'none',
											color: '#6c757d'
										}}></i>
									</div>
									<span className="text-muted">to</span>
									<div className="position-relative">
										<input
											type="date"
											className="form-control form-control-sm"
											style={{ width: '150px', paddingRight: '35px' }}
											value={globalEndDate ? dayjs(globalEndDate).format("YYYY-MM-DD") : ""}
											min={globalStartDate ? dayjs(globalStartDate).format("YYYY-MM-DD") : ""}
											max={dayjs().format("YYYY-MM-DD")}
											onChange={(e) => handleGlobalDateChange("end", e.target.value)}
										/>
										<i className="ti ti-calendar position-absolute" style={{
											right: '10px',
											top: '50%',
											transform: 'translateY(-50%)',
											pointerEvents: 'none',
											color: '#6c757d'
										}}></i>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* First Row: Attendance, Meals, and Calendar */}
					<div className="row mb-4">
						{/* Attendance Card */}
						<div className="col-md-4 d-flex">
							<div className="card flex-fill">
								<div className="card-header d-flex justify-content-between">
									<h4 className="card-title">
										Attendance
										{attendanceLoading && <span className="spinner-border spinner-border-sm ms-2" />}
									</h4>
									<div className="card-dropdown">
										<button
											className="btn dropdown-toggle p-0 border-0 bg-transparent"
											type="button"
											data-bs-toggle="dropdown"
											aria-expanded={attendanceDropdownOpen}
											onClick={() => setAttendanceDropdownOpen(!attendanceDropdownOpen)}
											disabled={isGlobalFilterActive}
										>
											<i className="ti ti-calendar-due" />
											{getDisplayText(attendanceDateRange, customAttendanceStartDate, customAttendanceEndDate)}
										</button>
										<ul className="dropdown-menu dropdown-menu-end">
											{Object.keys(displayText).map(r => (
												<li key={r}>
													<button
														className="dropdown-item"
														type="button"
														onClick={() => handleAttendanceRangeChange(r)}
													>
														{displayText[r as keyof typeof displayText]}
													</button>
												</li>
											))}
										</ul>
									</div>
								</div>
								<div className="card-body">
									<div className="border rounded p-3 mb-3">
										<div className="row text-center">
											<div className="col border-end">
												<p className="mb-1">Present</p>
												<h5>{attendanceData.studentsPresent}</h5>
											</div>
											<div className="col">
												<p className="mb-1">Absent</p>
												<h5>{attendanceData.studentsAbsent}</h5>
											</div>
										</div>
									</div>
									{!attendanceLoading ? (
										<ReactApexChart
											options={attendanceChartOpts}
											series={attendanceChartOpts.series}
											type="donut"
											height={200}
										/>
									) : (
										<div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
											<div className="spinner-border" role="status">
												<span className="visually-hidden">Loading...</span>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Meals Card */}
						<div className="col-md-4 d-flex">
							<div className="card flex-fill">
								<div className="card-header d-flex justify-content-between">
									<h4 className="card-title">
										Meals
										{mealsLoading && <span className="spinner-border spinner-border-sm ms-2" />}
									</h4>
									<div className="card-dropdown">
										<button
											className="btn dropdown-toggle p-0 border-0 bg-transparent"
											type="button"
											data-bs-toggle="dropdown"
											aria-expanded={mealsDropdownOpen}
											onClick={() => setMealsDropdownOpen(!mealsDropdownOpen)}
											disabled={isGlobalFilterActive}
										>
											<i className="ti ti-calendar-due" />
											{getDisplayText(mealsDateRange, customMealsStartDate, customMealsEndDate)}
										</button>
										<ul className="dropdown-menu dropdown-menu-end">
											{Object.keys(displayText).map(r => (
												<li key={r}>
													<button
														className="dropdown-item"
														type="button"
														onClick={() => handleMealsRangeChange(r)}
													>
														{displayText[r as keyof typeof displayText]}
													</button>
												</li>
											))}
										</ul>
									</div>
								</div>
								<div className="card-body">
									<div className="border rounded p-3 mb-3">
										<div className="row text-center">
											<div className="col border-end">
												<p className="mb-1">Served</p>
												<h5>{mealsData.mealsServedCount}</h5>
											</div>
											<div className="col">
												<p className="mb-1">Not Served</p>
												<h5>{mealsData.melasNotServedCount}</h5>
											</div>
										</div>
									</div>
									{!mealsLoading ? (
										<ReactApexChart
											options={mealsChartOpts}
											series={mealsChartOpts.series}
											type="donut"
											height={200}
										/>
									) : (
										<div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
											<div className="spinner-border" role="status">
												<span className="visually-hidden">Loading...</span>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Calendar */}
						<div className="col-md-4">
							<div className="card flex-fill animate-card border-0">
								<div className="card-body">
									<div className="d-flex align-items-center">
										<div className="avatar avatar-xl me-2 bg-secondary-transparent p-1">
											<ImageWithBasePath
												src="assets/img/icons/teacher.svg"
												alt="img"
											/>
										</div>
										<div className="overflow-hidden flex-fill">
											<div className="d-flex align-items-center justify-content-between">
												<h3 className="counter">
													Teacher Check In
												</h3>
												<button
													className="btn btn-primary btn-sm"
													disabled={isProcessing}
													onClick={() => {
														if (isLocationVerified) {
															if (isCheckedIn) {
																teacherAttnCheckOut();
															} else {
																teacherAttnCheckIn();
															}
														} else {
															setShowGeoLocationModal(true);
														}
													}}
												>
													{isProcessing ? (
														<>
															<i className="ti ti-loader me-1 spin" />
															Processing...
														</>
													) : (
														<>
															<i className={isCheckedIn ? "ti ti-map-pin-check me-1" : "ti ti-map-pin-off me-1"} />
															{isCheckedIn ? 'Check Out' : 'Check In'}
														</>
													)}
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="card flex-fill">
								<div className="card-header d-flex justify-content-between">
									<h4 className="card-title">Schedules</h4>
									{calendarDate && (
										<button
											className="btn btn-sm btn-outline-secondary"
											onClick={() => {
												setCalendarDate(null);
												setIsGlobalFilterActive(true);
												const startStr = dayjs(globalStartDate).format('YYYY-MM-DD');
												const endStr = dayjs(globalEndDate).format('YYYY-MM-DD');
												fetchAllData(startStr, endStr);
											}}
										>
											Clear
										</button>
									)}
								</div>
								<div className="card-body">
									<Calendar
										inline
										value={calendarDate}
										onChange={handleCalendarChange}
										className="custom-cal-react"
										maxDate={new Date()}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Second Row: Class List */}
					<div className="row">
						<div className="col-xxl-6 col-xl-6 col-md-6">
							<div className="card">
								<div className="card-header d-flex align-items-center justify-content-between">
									<h4 className="card-title">
										Class List
										{classListLoading && <span className="spinner-border spinner-border-sm ms-2" />}
									</h4>
									<Link to={routes.studentList || "#"} className="link-primary fw-medium">
										View All
									</Link>
								</div>
								{renderClassList()}
							</div>
						</div>
					</div>
				</div>
			</div>
			<AdminDashboardModal />

			{/* GeoLocation Modal */}
			{showGeoLocationModal && (
				<GeoLocationModal
					show={showGeoLocationModal}
					onClose={() => setShowGeoLocationModal(false)}
					onLocationVerified={handleLocationVerified}
					allowedRadius={150}
				/>
			)}

			{/* Error Modal */}
			{errorMessage && (
				<ErrorModal
					show={showErrorModal}
					message={errorMessage ?? ""}
					onClose={() => setShowErrorModal(false)}
				/>
			)}
		</>
	);
};

export default TeacherDashboard;